import React from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2, Upload, X } from 'lucide-react';
import { useSandboxPreviewBannerController } from '../hooks/useSandboxPreviewBannerController';
import SandboxPreviewBanner from './SandboxPreviewBanner';
import SandboxChangesPanel from './SandboxChangesPanel';
import BranchReviewDecision from './BranchReviewDecision';
import BranchReviewArchive from './BranchReviewArchive';
import ApprovalReview from '../../approvals/ApprovalReview';

/** One branch overlay across normal workspace pages. */
export default function ConfigureSandboxPreviewChrome({ children, canOpenEditors = true }) {
  const controller = useSandboxPreviewBannerController();
  return <ConfigureSandboxPreviewContent controller={controller} canOpenEditors={canOpenEditors}>{children}</ConfigureSandboxPreviewContent>;
}

export function ConfigureSandboxPreviewContent({ children, canOpenEditors = true, controller, readOnly = false }) {
  const location = useLocation();
  const {
    isPreviewActive,
    previewBannerProps,
    showChangesPanel,
    showPublicationPanel,
    togglePublicationPanel,
    reviewState,
  } = controller;
  if (!isPreviewActive) return children;
  const { branch, review, context, ready, error, query, artifactsQuery } = reviewState;
  const emptyBuildPreview = location.pathname === '/app/build' &&
    !error && !query.isLoading && Boolean(review) && !context.revisionId;
  const approvalId = context.approvalId || (review?.publication?.kind === 'sandbox_publication' ? review.publication.approval_id : null);
  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="configure-sandbox-preview-chrome">
      <SandboxPreviewBanner
        {...previewBannerProps}
        publicationControl={readOnly ? <button type="button" onClick={previewBannerProps.onPublish}
          disabled={!ready || !previewBannerProps.onPublish}
          className="inline-flex items-center gap-1.5 rounded-lg bg-cyan-900 px-3 py-2 text-xs font-semibold text-white transition hover:bg-cyan-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-700 focus-visible:ring-offset-2 disabled:opacity-40">
          <Upload size={14} aria-hidden="true" />Approve and apply
        </button> : <>
          <BranchReviewDecision
            key={context.branchId + ':' + context.revisionId + ':' + context.contentHash}
            branch={branch}
            review={review}
            context={context}
            ready={ready && !readOnly}
            emptyReview={emptyBuildPreview}
            onPublished={controller.clearPreview}
            onShowDetails={togglePublicationPanel}
            detailsOpen={showPublicationPanel}
          />
          {reviewState.canArchive && <BranchReviewArchive
            branchId={context.branchId}
            pendingApproval={branch?.sandbox_status === 'approval_pending'}
          />}
        </>}
      />
      {showPublicationPanel && (ready || emptyBuildPreview) && (
        <section
          aria-label="Change approval"
          className="max-h-[45vh] shrink-0 overflow-y-auto border-b border-slate-200 bg-white p-4"
        >
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-800">Approval details</span>
            <button aria-label="Close approval details" onClick={togglePublicationPanel}>
              <X size={16} />
            </button>
          </div>
          {review.validation?.errors?.length > 0 && (
            <ul className="mb-3 list-disc pl-5 text-xs text-amber-800">
              {review.validation.errors.map((issue, i) => (
                <li key={i}>{typeof issue === 'string' ? issue : issue.message || JSON.stringify(issue)}</li>
              ))}
            </ul>
          )}
          {emptyBuildPreview ? <p className="text-xs text-slate-600">No saved changes yet. Ask your agent to make the first change.</p> : approvalId ? <ApprovalReview
            approvalId={approvalId}
            compact
            embeddedReview
            hidePublicationAction
            workingReviewId={review?.working_review_id} expectedRevisionId={context.revisionId}
            expectedBranchId={context.branchId}
            decisionsEnabled={ready && !readOnly && !['published', 'archived', 'discarded'].includes(branch?.sandbox_status)}
          /> : <p className="text-xs text-slate-600">Approve and apply makes these changes live and records your approval. No separate request is needed.</p>}
        </section>
      )}
      <div className="min-h-0 flex-1 overflow-hidden">
        {error ? (
          <div role="alert" className="p-6 text-sm text-rose-800">
            <p>{error.message}</p>
            <button
              className="mt-3 underline"
              onClick={() => {
                query.refetch();
                artifactsQuery.refetch();
              }}
            >
              Try again
            </button>
          </div>
        ) : emptyBuildPreview && showChangesPanel ? (
          <p className="p-6 text-sm text-slate-500">No saved changes yet.</p>
        ) : !ready && !emptyBuildPreview ? (
          <p className="flex items-center gap-2 p-6 text-sm text-slate-500">
            <Loader2 size={16} className="animate-spin" />
            {reviewState.loadingLabel || 'Loading preview…'}
          </p>
        ) : showChangesPanel ? (
          <SandboxChangesPanel
            readOnly={readOnly}
            key={context.branchId + ':' + context.revisionId}
            sandbox={branch}
            changesData={reviewState.changes}
            context={context}
            definitions={reviewState.artifacts}
            canOpenEditors={canOpenEditors}
          />
        ) : (
          <React.Fragment key={context.branchId + ':' + context.revisionId}>{children}</React.Fragment>
        )}
      </div>
    </div>
  );
}
